import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/cn'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-sm border px-2 py-0.5 text-xs font-medium font-sans w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:ring-2 focus-visible:ring-salmon focus-visible:ring-offset-2 focus-visible:ring-offset-paper aria-invalid:ring-burn/20 aria-invalid:border-burn transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-ink text-paper',
        secondary:
          'bg-vellum text-ink border border-rule',
        destructive:
          'border-transparent bg-burn text-white',
        outline:
          'text-ink border-rule',
        // Semantic financial variants
        gain:
          'bg-gain/10 text-gain border border-gain/20',
        burn:
          'bg-burn/10 text-burn border border-burn/20',
        salmon:
          'bg-salmon/10 text-salmon-text border border-salmon/30',
        // Legacy Verbal provider variants — preserved for existing usages
        success:
          'border-chart-success bg-chart-success/10 text-chart-success',
        warning:
          'border-chart-warning/30 bg-chart-warning/10 text-chart-warning',
        error:
          'border-burn bg-burn/10 text-burn',
        openai:
          'border-provider-openai bg-provider-openai/10 text-provider-openai',
        anthropic:
          'border-provider-anthropic bg-provider-anthropic/10 text-provider-anthropic',
        google:
          'border-provider-google bg-provider-google/10 text-provider-google',
        copilot:
          'border-provider-copilot bg-provider-copilot/10 text-provider-copilot',
        cursor:
          'border-provider-cursor bg-provider-cursor/10 text-provider-cursor',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
